<template>
  <div v-if="!loading" key="profile-loaded">
    <div class="profile">
      <af-button secondary="true" @clicked="downloadCV">
        <Icon :size="16" slot="icon" type="download"></Icon>
        Ladda ned
      </af-button>

      <div class="profile-content">
        <section class="profile-row">
          <section class="section-personal">
            <af-h1 class="section-title">{{ name }}</af-h1>
            <list :items="personalInformation" class="profile__list-items"></list>

            <div class="popover-wrap">
              <af-popover>
                <template slot="title">Varför får jag inte se fler personuppgifter?</template>
                <template slot="message">
                 Våra användares integritet är viktig. Därför visar vi bara de personuppgifter som behövs för att du ska kunna bedöma deras kompetens. Du kontaktar användarna enkelt genom att skicka ett meddelande.
                </template>
              </af-popover>
            </div>

          <div class="message-button">
            <af-button @clicked="openMessageModal" buttonStyle="nis-primary" lowercase size="large">
              Skicka meddelande
            </af-button>
          </div>
          </section>

          <profile-info
            v-if="isSeekingAsylum && !hasATUND"
            class="profile-info--no-at-und">
              <template slot="title">
                Detta är en person som inte får jobba än
              </template>
              <template slot="content">
                <p>
                  En person som ännu inte har fått ett bevis om AT-UND <em>(undantag från kravet på arbetstillstånd)</em> på sitt LMA-kort får inte jobba i Sverige.
                </p>
                <p>
                  Vill du anställa en person som ännu inte fått bevis AT-UND?  Be personen kontakta Migrationsverket för att fråga om hen kommer att få AT-UND.
                </p>
              </template>
            <template slot="link">
              Läs mer om att anställa en person som inte har rätt att arbeta än
            </template>
          </profile-info>

          <profile-info
            v-if="isSeekingAsylum && hasATUND">
            <template slot="title">
              Detta är en person som får jobba
            </template>
            <p slot="content">
              En person får jobba i Sverige om personen har ett bevis om AT-UND <em>(undantag från kravet på arbetstillstånd)</em> på sitt LMA-kort.
            </p>
            <template slot="link">
              Läs mer om att anställa en person som har rätt att arbeta
            </template>
          </profile-info>

          <profile-info
            v-if="hasResidencePermit">
            <template slot="title">
              Detta är en person med uppehållstillstånd
            </template>
            <p slot="content">
              En person med uppehållstillstånd kan anställas precis som vanligt. Det finns inga speciella regler eller krav du behöver förhålla dig till som arbetsgivare. I vissa fall kan du få ekonomiskt stöd när du anställer nyanlända personer.
            </p>
            <template slot="link">
              Läs mer om att anställa en person med uppehållstillstånd
            </template>
          </profile-info>
        </section>

        <section class="profile-row">
          <section class="section-work">
            <af-h2>Yrkeserfarenheter</af-h2>
            <experience-row
              v-for="(row, index) in experiences"
              :key="'experience-' + index + row.taxonomy_id"
              :subtitle="row.years"
              :title="row.name"
              suffix="år">
            </experience-row>
          </section>

          <section class="section-career">
            <af-h2>Övriga yrkesönskemål</af-h2>
            <experience-row
              v-for="(row, index) in career"
              :key="'career-' + index + row.taxonomy_id"
              :title="row.name">
            </experience-row>
          </section>
        </section>

        <section class="profile-row">
          <section class="section-skills">
            <af-h2>Kompetenser</af-h2>
            <experience-row
              v-for="(row, index) in skills"
              :key="'skill-' + index + row.taxonomy_id"
              :subtitle="row.years"
              :title="row.name"
              suffix="år">
            </experience-row>
          </section>

          <section class="section-education">
            <af-h2>Utbildning</af-h2>
            <experience-row
              v-for="(row, index) in educations"
              :key="'edu-' + index + row.taxonomy_id"
              :subtitle="row.name"
              :title="row.educationSubject.name"
              strongTitle>
              <ul>
                <li>
                  <span v-if="row.years">{{ row.years }} år</span>
                  <span v-if="row.hasDegree">
                    <span v-if="row.years">-</span>
                    Har examen
                  </span>
                  <span v-if="!row.hasDegree">
                    <span v-if="row.years">-</span>
                    Har inte examen
                  </span>
                </li>
                <li v-if="row.hasDocuments">Har dokument</li>
                <li v-if="row.hasDocumentsEnglish">Har dokument på engelska</li>
              </ul>
            </experience-row>
          </section>
        </section>

        <section class="profile-row">
          <section class="section-language-skills">
            <af-h2>Språkkunskaper</af-h2>
            <list :items="languages" class="profile__list-items"></list>
          </section>
        </section>
      </div>
    </div>

    <modal name="ModalMessage">
      <modal-content>
        <af-information-notification slot="notification">
          <template slot="content">
            <p>Ditt meddelande skickas till användarens mejladress och svaret går till din mejl.</p>

            <p>
              Självklart får du inte uttrycka något hot eller formulera dig så att det kan uppfattas som stötande.  Tänk på att du som arbetsgivare är personuppgiftsansvarig för de uppgifter som du behandlar om användaren utanför Jobskills.
            </p>
          </template>
        </af-information-notification>

        <header slot="title">
          Kontakta profil #{{user.id}}
        </header>
        <section slot="content">
          <message-form :userId="user.id" />
        </section>
      </modal-content>
    </modal>
  </div>
  <div v-else class="body" key="profile-loading">
    <div class="loading">
      <icon spin type="circle-o-notch"></icon>
    </div>
  </div>
</template>

<script>
import ExperienceRow from '@/components/ExperienceRow'
import List from '@/components/List'
import Icon from '@/components/Icon'
import Breadcrumbs from '@/components/Breadcrumbs'
import AfH1 from '@/components/AfH1'
import Modal from '@/components/Modal'
import ModalContent from '@/components/ModalContent'
import AfInformationNotification from '@/components/AfInformationNotification'
import AfH2 from '@/components/AfH2'
import AfPopover from '@/components/AfPopover'
import Button from '@/components/Button/Button'
import MessageForm from './MessageForm'
import ProfileInfo from './ProfileInfo'
import { viewUser } from '@/queries'
import { unique, sortByName } from '@/utils/helpers'
import { mapGetters } from 'vuex'
import * as types from '@/store/actionTypes'
import cookieUtils from '@/utils/cookies'

export default {
  name: 'profile',

  components: {
    ExperienceRow,
    Icon,
    List,
    Breadcrumbs,
    'af-button': Button,
    'af-h1': AfH1,
    'af-h2': AfH2,
    'af-popover': AfPopover,
    'af-information-notification': AfInformationNotification,
    'message-form': MessageForm,
    Modal,
    ModalContent,
    ProfileInfo
  },
  data() {
    return {
      user: {},
      loading: 0
    }
  },
  apollo: viewUser,
  computed: {
    ...mapGetters(['hasPermissionUnlimitedSearch']),
    name() {
      return `Profil #${this.user.id}`
    },
    personalInformation() {
      if (!Object.keys(this.user).length) {
        return []
      }

      const {
        seeking_asylum,
        has_at_und,
        has_residence_permit,
        city
      } = this.user

      const cityName = city ? city.name : null

      const eje = [
        cityName ? { label: 'Kommun:', value: cityName } : { label: 'Kommun:', value: 'Ej angivet' },
        seeking_asylum && !has_at_und ? 'Jag har inte rätt att jobba än' : false,
        seeking_asylum && has_at_und ? 'Jag har rätt att jobba' : false,
        has_residence_permit ? 'Jag har uppehållstillstånd' : false
      ].filter(Boolean)

      return eje
    },
    languages() {
      if (!this.user.languageSkills) {
        return []
      }

      return this.user.languageSkills
        .filter(unique)
        .map(language => language.name)
        .sort()
    },
    educations() {
      if (!this.user.education) {
        return []
      }

      return this.user.education.slice().sort(sortByName)
    },
    experiences() {
      if (!this.user.experience) {
        return []
      }

      return this.user.experience.filter(unique).sort(sortByName)
    },
    skills() {
      if (!this.user.skills) {
        return []
      }

      return this.user.skills.filter(unique).sort(sortByName)
    },
    career() {
      if (!this.user.career) {
        return []
      }

      return this.user.career.filter(unique).sort(sortByName)
    },
    isSeekingAsylum() {
      return this.user.seeking_asylum
    },
    hasResidencePermit() {
      return this.user.has_residence_permit
    },
    hasATUND() {
      return this.user.has_at_und
    }
  },

  methods: {
    async downloadCV() {
      const data = {
        token: cookieUtils.getItem('search-token'),
        include: [
          'asylumStatus',
          'career',
          'education',
          'experience',
          'languageSkills',
          'skills',
          'city'
        ],
        language: 'sv'
      }

      const response = await fetch(
        `${window.__API_URL__}jobseekerpdf/${this.user.id}`,
        {
          headers: { 'content-type': 'application/json' },
          method: 'POST',
          body: JSON.stringify(data)
        }
      )

      const blob = await response.blob()
      const filename = 'jobskills-profile.pdf'

      // IE hack: "Access is denied"
      if (typeof window.navigator.msSaveBlob !== 'undefined') {
        window.navigator.msSaveBlob(blob, filename)
      } else {
        const link = document.createElement('a')
        link.href = window.URL.createObjectURL(blob)
        link.download = filename

        if (document.body) {
          document.body.appendChild(link)
        }

        link.click()

        if (document.body) {
          document.body.removeChild(link)
        }
      }
    },
    openMessageModal() {
      this.$store.commit(types.OPEN_MODAL, 'ModalMessage')
    }
  }
}
</script>
<style scoped src="./Profile.css">
</style>



// WEBPACK FOOTER //
// src/views/Profile/Profile.vue